import sharedDb from './db.js';

/**
 * Sync status: 'idle' | 'syncing' | 'offline' | 'error'
 */

const SYNC_CONFIG_KEY = 'sync-config';

/**
 * Get sync configuration from local DB
 */
export async function getSyncConfig() {
  return sharedDb.syncConfig.get(SYNC_CONFIG_KEY);
}

/**
 * Save sync configuration
 */
export async function saveSyncConfig(config) {
  await sharedDb.syncConfig.put({ id: SYNC_CONFIG_KEY, ...config });
}

/**
 * Check if sync is configured
 */
export async function isSyncConfigured() {
  const config = await getSyncConfig();
  return Boolean(config?.apiEndpoint);
}

/**
 * Record a pending change for offline sync
 */
export async function recordChange(table, recordId, operation) {
  await sharedDb.pendingChanges.add({
    table,
    recordId,
    operation, // 'put' | 'delete'
    timestamp: new Date().toISOString(),
  });
  // Notify listeners that a change was recorded
  window.dispatchEvent(new CustomEvent('sync-change-recorded'));
}

/**
 * Get all pending changes
 */
export async function getPendingChanges() {
  return sharedDb.pendingChanges.orderBy('seq').toArray();
}

/**
 * Clear a pending change after successful sync
 */
export async function clearPendingChange(seq) {
  await sharedDb.pendingChanges.delete(seq);
}

/**
 * Get a pre-signed URL from the API
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 * @param {string} key - The S3 object key
 * @param {string} operation - 'put' or 'get'
 * @param {string} [contentType] - MIME type for upload operations
 * @returns {Promise<string>} The pre-signed URL
 */
export async function getPresignedUrl(apiEndpoint, key, operation, contentType) {
  const res = await fetch(`${apiEndpoint}/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, operation, contentType }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Presign failed: ${res.status} ${text}`);
  }
  const data = await res.json();
  return data.url;
}

/**
 * Upload JSON data to S3
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 * @param {string} key - The S3 object key
 * @param {*} data - The data to serialize and upload
 */
export async function uploadJson(apiEndpoint, key, data) {
  const url = await getPresignedUrl(apiEndpoint, key, 'put', 'application/json');
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
}

/**
 * Download JSON from S3
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 * @param {string} key - The S3 object key
 * @returns {Promise<*|null>} The parsed JSON data, or null if not found
 */
export async function downloadJson(apiEndpoint, key) {
  try {
    const url = await getPresignedUrl(apiEndpoint, key, 'get');
    const res = await fetch(url);
    if (!res.ok) {
      if (res.status === 404 || res.status === 403) return null;
      throw new Error(`Download failed: ${res.status}`);
    }
    return res.json();
  } catch (err) {
    // If the file doesn't exist yet, that's fine
    if (err.message && err.message.includes('Presign failed')) {
      console.warn(`Could not download ${key}:`, err.message);
      return null;
    }
    throw err;
  }
}

/**
 * Upload a photo blob to S3
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 * @param {string} key - The S3 object key
 * @param {Blob} blob - The photo blob data
 * @param {string} mimeType - The MIME type of the photo
 */
export async function uploadPhoto(apiEndpoint, key, blob, mimeType) {
  const url = await getPresignedUrl(apiEndpoint, key, 'put', mimeType);
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': mimeType },
    body: blob,
  });
  if (!res.ok) throw new Error(`Photo upload failed: ${res.status}`);
}

/**
 * Download a photo from S3
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 * @param {string} key - The S3 object key
 * @returns {Promise<Blob|null>} The photo blob, or null if not found
 */
export async function downloadPhoto(apiEndpoint, key) {
  try {
    const url = await getPresignedUrl(apiEndpoint, key, 'get');
    const res = await fetch(url);
    if (!res.ok) {
      if (res.status === 404 || res.status === 403) return null;
      throw new Error(`Photo download failed: ${res.status}`);
    }
    return res.blob();
  } catch (err) {
    console.warn(`Could not download photo ${key}:`, err.message);
    return null;
  }
}

/**
 * List remote objects with a prefix
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 * @param {string} prefix - The S3 key prefix to list
 * @returns {Promise<Array>} Array of objects with key metadata
 */
export async function listRemoteObjects(apiEndpoint, prefix) {
  const res = await fetch(`${apiEndpoint}/list?prefix=${encodeURIComponent(prefix)}`);
  if (!res.ok) throw new Error(`List failed: ${res.status}`);
  const data = await res.json();
  return data.objects || [];
}

/**
 * Push all species from the shared DB to S3.
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 */
export async function pushSpecies(apiEndpoint) {
  const species = await sharedDb.plantSpecies.toArray();
  await uploadJson(apiEndpoint, 'data/plantSpecies.json', species);
}

/**
 * Pull species from S3 and merge into the shared DB.
 * Deduplicates by both ID and scientificName to prevent duplicates from multi-device seeding.
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 */
export async function pullSpecies(apiEndpoint) {
  const remoteSpecies = await downloadJson(apiEndpoint, 'data/plantSpecies.json');
  if (!remoteSpecies) return;

  for (const remote of remoteSpecies) {
    // First check by ID (exact match)
    const localById = await sharedDb.plantSpecies.get(remote.id);
    if (localById) {
      // Same ID exists — merge fields (keep local values that remote doesn't have)
      if (remote.updatedAt > localById.updatedAt) {
        const merged = { ...localById };
        for (const [key, value] of Object.entries(remote)) {
          // Overwrite with remote value if remote has a non-empty value,
          // or if local doesn't have this field at all
          if (value !== null && value !== undefined && value !== '') {
            merged[key] = value;
          }
        }
        // Preserve local _sources if remote doesn't have them
        if (localById._sources && !remote._sources) {
          merged._sources = localById._sources;
        } else if (localById._sources && remote._sources) {
          merged._sources = { ...localById._sources, ...remote._sources };
        }
        merged.updatedAt = remote.updatedAt;
        await sharedDb.plantSpecies.put(merged);
      }
      continue;
    }

    // Check by scientificName to avoid duplicates from different devices
    if (remote.scientificName) {
      const localByName = await sharedDb.plantSpecies
        .where('scientificName')
        .equalsIgnoreCase(remote.scientificName)
        .first();
      if (localByName) {
        // Same species exists with different ID — merge if remote is newer
        if (remote.updatedAt > (localByName.updatedAt || '')) {
          const merged = { ...localByName };
          for (const [key, value] of Object.entries(remote)) {
            if (value !== null && value !== undefined && value !== '') {
              merged[key] = value;
            }
          }
          if (localByName._sources && !remote._sources) {
            merged._sources = localByName._sources;
          } else if (localByName._sources && remote._sources) {
            merged._sources = { ...localByName._sources, ...remote._sources };
          }
          merged.id = remote.id;
          merged.updatedAt = remote.updatedAt;
          await sharedDb.plantSpecies.delete(localByName.id);
          await sharedDb.plantSpecies.put(merged);
        }
        continue;
      }
    }

    // Truly new species — insert
    await sharedDb.plantSpecies.put(remote);
  }
}

/**
 * Process all pending species changes: push current species to S3, then clear the queue
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 */
export async function syncPendingChanges(apiEndpoint) {
  const pending = await getPendingChanges();
  if (pending.length === 0) return;

  // Push current species state to S3
  await pushSpecies(apiEndpoint);

  // Clear all processed pending changes
  for (const change of pending) {
    await clearPendingChange(change.seq);
  }
}

/**
 * Full sync: pull latest from remote first, merge locally, then push merged state
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 */
export async function fullSync(apiEndpoint) {
  // Process any pending changes first (clears the queue)
  const pending = await getPendingChanges();
  if (pending.length > 0) {
    for (const change of pending) {
      await clearPendingChange(change.seq);
    }
  }

  // Pull remote species first so we merge before pushing
  await pullSpecies(apiEndpoint);

  // Now push the merged state (local + remote) back to S3
  const mergedSpecies = await sharedDb.plantSpecies.toArray();
  console.log('[fullSync] After pull+merge, pushing', mergedSpecies.length, 'species to S3');
  await pushSpecies(apiEndpoint);
}
